import { RichTextContent } from '@/components/molecules/rich-text-content';
import { hasReadableContent } from '@/lib/terms/clientTerms';
import { proposalOptionOffers } from '@/lib/sales/proposalOptionBreakdown';
import type { ProposalOption } from '@/types/proposal';
import { LineItemPhoto } from './LineItemPhoto';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The same optional choices and stable tokens on every proposal design. */
export function ProposalOptionalSelection({
  option,
  disabled = false,
  showImages = false,
  onToggleOptional,
}: {
  option: ProposalOption;
  disabled?: boolean;
  showImages?: boolean;
  onToggleOptional: (offerKey: string) => void;
}) {
  const offeredOffers = proposalOptionOffers(option);
  if (!offeredOffers.length) return null;
  const visiblePrices = offeredOffers.filter((offer) => offer.amount != null);
  const listPrices =
    visiblePrices.length > 0 &&
    visiblePrices.every((offer) => offer.parent.optionalSubtotal != null);
  return (
    <div>
      <p className="mb-2 text-xs text-[#63646B]">
        Choose the add-ons you want included in your proposal.{' '}
        {listPrices ? 'Add-on prices are before discounts. ' : ''}
        Review your total before approving.
      </p>
      <ul className="flex flex-col gap-2">
        {offeredOffers.map((offer) => {
          const selected = offer.parent.isSelected !== false;
          return (
            <li key={offer.key}>
              <button
                type="button"
                onClick={() => onToggleOptional?.(offer.key)}
                aria-pressed={selected}
                disabled={disabled || !offer.canToggle}
                className={`flex w-full items-center gap-3 rounded-[10px] border px-3.5 py-3 text-left transition-colors disabled:opacity-50 ${
                  selected
                    ? 'border-[color:var(--ss-accent,#B4491F)] bg-[color:var(--ss-accent,#B4491F)]/[0.06]'
                    : 'border-[#14141A]/[0.13] bg-white hover:border-[#14141A]/25'
                }`}
              >
                <span
                  className={`flex h-5 w-5 flex-none items-center justify-center rounded-[6px] border text-[12px] font-semibold ${
                    selected
                      ? 'border-[color:var(--ss-accent,#B4491F)] bg-[color:var(--ss-accent,#B4491F)] text-white'
                      : 'border-[#14141A]/30 text-transparent'
                  }`}
                  aria-hidden
                >
                  {selected ? '✓' : '+'}
                </span>
                {showImages ? <LineItemPhoto line={offer.parent} /> : null}
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-[#14141A]">
                    {offer.parent.name}
                  </span>
                  <span className="block text-[12px] text-[#63646B]">
                    {selected ? 'Included' : 'Not included'}
                  </span>
                  {offer.parent.description && hasReadableContent(offer.parent.description) ? (
                    <RichTextContent
                      as="span"
                      content={offer.parent.description}
                      className="mt-0.5 block text-[12px] leading-[1.5] text-[#63646B]"
                    />
                  ) : null}
                </span>
                <span className="flex-none text-right text-[12px] text-[#63646B]">
                  <span className="block font-semibold tabular-nums text-[#14141A]">
                    {offer.amount != null
                      ? `${selected ? '' : '+'}${fmt(offer.amount)}`
                      : selected
                        ? 'Price included in total'
                        : 'Adding updates the total'}
                  </span>
                  {offer.canToggle ? <span>{selected ? 'Remove' : 'Add'}</span> : null}
                </span>
              </button>
              {!offer.canToggle && (
                <p className="mt-1 text-xs text-gray-500">Contact us to change this add-on.</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
